import type { ChosenCompany } from '../../api/signup';
import { isEmail } from '../LoginPage/loginView';

export type SignUpField = 'firstName' | 'lastName' | 'email' | 'password' | 'repeatPassword' | 'company' | 'terms';
export type SignUpValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  repeatPassword: string;
  company: ChosenCompany | null;
  terms: boolean;
};
export type SignUpError = { field: SignUpField | 'general'; message: string };

export const MIN_PASSWORD_LENGTH = 8;

export const EMPTY_VALUES: SignUpValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  repeatPassword: '',
  company: null,
  terms: false,
};

/** The field in the form each error leads to */
export const FIELD_IDS: Record<SignUpField, string> = {
  firstName: 'signup-firstName',
  lastName: 'signup-lastName',
  email: 'signup-email',
  password: 'signup-password',
  repeatPassword: 'signup-repeatPassword',
  company: 'signup-company',
  terms: 'signup-terms',
};

/** Organisasjonsnummer skrives ofte med mellomrom, for eksempel «123 456 789» */
export const cleanOrgNumber = (value: string) => value.replace(/\s/g, '');

/** «923 609 016» */
export const formatOrgNumber = (value: string) => value.replace(/^(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3');

/** Nine digits, and the last is the modulus 11 check digit Enhetsregisteret uses, as the server checks it */
export const isValidOrgNumber = (value: string) => {
  if (!/^\d{9}$/.test(value)) return false;
  const weights = [3, 2, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((total, weight, index) => total + weight * Number(value[index]), 0);
  const rest = sum % 11;
  const check = rest === 0 ? 0 : 11 - rest;
  return check !== 10 && check === Number(value[8]);
};

/** Nine digits that cannot be an org number: a digit is wrong, so there is nothing to look up */
export const wrongCheckDigit = (query: string) => {
  const cleaned = cleanOrgNumber(query);
  return /^\d{9}$/.test(cleaned) && !isValidOrgNumber(cleaned);
};

/** Only digits, and fewer than nine: the user is typing a number, not a name */
export const missingDigits = (query: string) => {
  const cleaned = cleanOrgNumber(query);
  return /^\d+$/.test(cleaned) && cleaned.length < 9 ? cleaned.length : null;
};

/** Feilene i skjemaet, i samme rekkefølge som feltene */
export const validateSignUp = (values: SignUpValues): SignUpError[] => {
  const errors: SignUpError[] = [];
  const add = (field: SignUpField, message: string) => errors.push({ field, message: `auth.signUp.errors.${message}` });
  if (!values.firstName.trim()) add('firstName', 'firstName');
  if (!values.lastName.trim()) add('lastName', 'lastName');
  if (!values.email.trim()) add('email', 'email');
  else if (!isEmail(values.email.trim())) add('email', 'emailFormat');
  if (values.password.length < MIN_PASSWORD_LENGTH) add('password', 'passwordLength');
  if (!values.repeatPassword) add('repeatPassword', 'repeatPassword');
  else if (values.repeatPassword !== values.password) add('repeatPassword', 'mismatch');
  if (!values.company) add('company', 'company');
  if (!values.terms) add('terms', 'terms');
  return errors;
};

/** The fields the server refuses, with the field in the form and what it says, in the order of the form */
const INVALID_FIELDS: Array<[string, SignUpError]> = [
  ['firstName', { field: 'firstName', message: 'auth.signUp.errors.firstName' }],
  ['lastName', { field: 'lastName', message: 'auth.signUp.errors.lastName' }],
  ['email', { field: 'email', message: 'auth.signUp.errors.emailFormat' }],
  ['password', { field: 'password', message: 'auth.signUp.errors.passwordLength' }],
  ['orgNumber', { field: 'company', message: 'auth.signUp.errors.orgNumberInvalid' }],
  ['orgName', { field: 'company', message: 'auth.signUp.errors.orgName' }],
  ['terms', { field: 'terms', message: 'auth.signUp.errors.terms' }],
];

/** Feilkodene fra API-et når skjemaet sendes */
export const serverError = (data: { errorCode?: string; code?: string; fields?: unknown } | undefined): SignUpError => {
  if (data?.errorCode?.includes('UAE')) return { field: 'email', message: 'auth.signUp.errors.emailInUse' };
  if (data?.errorCode?.includes('ONIU')) return { field: 'company', message: 'auth.signUp.errors.orgNumberInUse' };
  if (data?.code === 'invalid' && Array.isArray(data.fields)) {
    // The server checks the same as the form, and says which field is wrong, such as a wrong check digit
    const invalid = INVALID_FIELDS.find(([key]) => (data.fields as unknown[]).includes(key));
    if (invalid) return invalid[1];
  }
  if (data?.code === 'newVersion') return { field: 'terms', message: 'auth.signUp.errors.newVersion' };
  if (data?.code === 'tooSoon' || data?.code === 'tooManyCodes')
    return { field: 'general', message: `auth.signUp.code.errors.${data.code}` };
  return { field: 'general', message: 'auth.signUp.errors.general' };
};

export type CodeError = 'format' | 'wrong' | 'expired' | 'tooManyAttempts' | 'tooSoon' | 'tooManyCodes' | 'password' | 'general';

/** What went wrong with the code, and how many tries are left */
export const codeError = (data: { code?: string; attemptsLeft?: number } | undefined): { error: CodeError; attemptsLeft?: number } => {
  const known: CodeError[] = ['wrong', 'expired', 'tooManyAttempts', 'tooSoon', 'tooManyCodes'];
  const code = data?.code as CodeError | undefined;
  if (code && known.includes(code)) return { error: code, attemptsLeft: data?.attemptsLeft };
  return { error: 'general' };
};

/** The step and the email survive a reload of the page. The password never leaves memory. */
const PENDING_KEY = 'signup.pending';

export const rememberPending = (email: string) => {
  try {
    sessionStorage.setItem(PENDING_KEY, email);
  } catch {
    // Without storage, a reload starts the form again
  }
};

export const pendingEmail = (): string | null => {
  try {
    return sessionStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
};

export const forgetPending = () => {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Nothing to forget
  }
};
